import { SEVERITY_ORDER, Severity } from '@insio/validation';
import type { ValidationIssue } from '../types';

/** A condensed issue suitable for single-slot UI (icon + tooltip). Other
 * fields from the canonical `ValidationIssue` are intentionally dropped —
 * code/path/groups have no meaning for a synthetic merged record.
 *
 * `meta` is preserved when the input list contains exactly one issue so
 * downstream renderers (`ErrorEntry`) can still surface `expected` / `actual`
 * context; it is dropped for multi-issue merges because no single meta record
 * applies to the joined message. */
export type CombinedIssue = Pick<ValidationIssue, 'severity' | 'message' | 'meta'>;

/**
 * Collapses a list of validation issues into a single one for display in
 * components that surface only one issue at a time (e.g. EditableField icon/tooltip).
 *
 * - Severity: the most severe one — `error`, then `warning`, then `info`.
 * - Message: each issue's message joined with ' · '.
 * - Meta: kept from the only issue when the list has length 1; otherwise omitted.
 *
 * Returns `undefined` when there are no issues, so consumers can spread it
 * directly into an optional `error` prop.
 */
export function combineIssues(issues: ValidationIssue[] | undefined): CombinedIssue | undefined {
  if (!issues || issues.length === 0) return undefined;
  const severity: ValidationIssue['severity'] =
    SEVERITY_ORDER.find(level => issues.some(i => i.severity === level)) ?? Severity.warning;
  const message = issues.map(i => i.message).join(' · ');
  const meta = issues.length === 1 ? issues[0]?.meta : undefined;
  return { severity, message, meta };
}
